import DynatraceClient from '@/plugins/dynatracePlugin';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useDebtSolutionsStore } from '@/modules/debtsolutions/store/debtsolutions.store';
import { RouteNames } from '@/router/routeNames';
import { useGlobalStore } from '@/stores/global/global.store';
import router from '@/router';

export const getNav = () => {
  const customerStore = useCustomerStore();
  const policyStore = usePolicyStore();
  const debtsolutionsStore = useDebtSolutionsStore();
  const globalStore = useGlobalStore();

  async function goToMyFinancialHome() {
    await router.push({ name: RouteNames.DASHBOARD });
  }

  async function investmentsAction() {
    if (hasInvestments()) {
      await router.push({ name: RouteNames.INVESTMENTS });
    } else {
      await router.push({ name: RouteNames.INVESTMENT_LEARN_MORE });
    }
  }

  async function termLifeAction() {
    if (policyStore.policies.length > 0) {
      await router.push({
        name: RouteNames.POLICY_DETAILS,
        params: { contractId: policyStore.selectedPolicyIndex },
      });
    } else {
      await router.push({ name: RouteNames.TERM_LIFE_BENEFITS });
    }
  }

  async function debtAction() {
    const hasActiveDebtCount = debtsolutionsStore.unpaidDebts.length > 0;
    if (hasActiveDebtCount) {
      await router.push({ name: RouteNames.MY_DEBT });
    } else {
      await router.push({ name: RouteNames.LEARN_MORE_DEBT });
    }
  }

  async function repAction() {
    // This is to handle beneficiary page not being shown then go to My Rep
    await router.push({ name: RouteNames.MY_REP });
  }

  async function documentsAction(isMobile: boolean) {
    try {
      const i = DynatraceClient.enterAction('link_documents_header');
      DynatraceClient.leaveAction(i);
    } catch (_error) {
      /* empty */
    }
    if (isMobile) {
      await router.push({ name: RouteNames.DOCUMENTS });
    } else {
      await router.push({ name: RouteNames.NOTICES_AND_CONFIRMATIONS }); // default route
    }
  }

  async function accountAction() {
    await router.push({ name: RouteNames.MY_ACCOUNT });
  }

  function hasLanguageFeatureFlag() {
    if (globalStore.containsFeature('LANGUAGE-TOGGLE')) {
      if (globalStore.localeLanguages.length > 1) {
        return true;
      }
    }
    return false;
  }

  function hasActiveDebt() {
    return debtsolutionsStore.unpaidDebts.length > 0;
  }

  function hasInvestments() {
    return (customerStore.investments.summary?.totalValue ?? 0) > 0;
  }

  function hasTermLife() {
    return policyStore.hasTermLife;
  }

  function isRouteDashboard() {
    return router.currentRoute.value.name === RouteNames.DASHBOARD;
  }
  function isRouteTermLifeBenefits() {
    return (
      router.currentRoute.value.name === RouteNames.TERM_LIFE_BENEFITS ||
      router.currentRoute.value.name === RouteNames.POLICY_DETAILS ||
      router.currentRoute.value.name === RouteNames.GUIDANCE_CALCULATOR
    );
  }
  function isRouteInvestments() {
    if (typeof router.currentRoute.value.name === 'string') {
      return (
        router.currentRoute.value.name.toLowerCase() === RouteNames.INVESTMENTS.toLowerCase() ||
        router.currentRoute.value.name.toLowerCase() ===
          RouteNames.INVESTMENT_LEARN_MORE.toLowerCase() ||
        router.currentRoute.value.name.toLowerCase() ===
          RouteNames.INVESTMENT_CALCULATOR.toLowerCase()
      );
    }
    return false;
  }
  function isRouteDebtSolutions() {
    return (
      router.currentRoute.value.name === RouteNames.DEBT_SOLUTIONS ||
      router.currentRoute.value.name === RouteNames.MY_DEBT ||
      router.currentRoute.value.name === RouteNames.MY_PLAN ||
      router.currentRoute.value.name === RouteNames.LEARN_MORE_DEBT
    );
  }
  function isRouteMyRep() {
    return router.currentRoute.value.name === RouteNames.MY_REP;
  }
  function isRouteDocuments() {
    return (
      router.currentRoute.value.name === RouteNames.DOCUMENTS ||
      router.currentRoute.value.name === RouteNames.DOCUMENT_LIST_MOBILE ||
      router.currentRoute.value.name === RouteNames.NOTICES_AND_CONFIRMATIONS ||
      router.currentRoute.value.name === RouteNames.POLICY_DOCUMENTS ||
      router.currentRoute.value.name === RouteNames.INVESTMENT_STATEMENTS
    );
  }
  function isRouteHelp() {
    return router.currentRoute.value.name === RouteNames.HELP;
  }
  function isRouteMyAccount() {
    return router.currentRoute.value.name === RouteNames.MY_ACCOUNT;
  }
  function isRouteAlertAndNotifications() {
    return router.currentRoute.value.name === RouteNames.ALERT_AND_NOTIFICATIONS;
  }

  return {
    goToMyFinancialHome,
    investmentsAction,
    termLifeAction,
    repAction,
    debtAction,
    documentsAction,
    accountAction,
    hasActiveDebt,
    hasInvestments,
    hasTermLife,
    hasLanguageFeatureFlag,
    isRouteDashboard,
    isRouteTermLifeBenefits,
    isRouteInvestments,
    isRouteDebtSolutions,
    isRouteMyRep,
    isRouteDocuments,
    isRouteMyAccount,
    isRouteAlertAndNotifications,
    isRouteHelp,
  };
};
